import DesignTokens from '@tableau/design-tokens';
import { CheckBoxWithLabelProps, CheckBoxWithLabelWidget, DropdownButtonType, List,SingleSelectDropdown, TabStyles } from '@tableau/widgets';
import { DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { SortableDataGrid } from '~src/commonInfra/components/dataGrid/SortableDataGrid3';
import { FileConnectionDetail } from '~src/datasource/dataConnection/serverApi/types/ConnectionDetail';
import { FlowOutputStepGrid } from '~src/flow/components/content/FlowOutputStepGrid';
import { FlowOutputStepInfo } from '~src/flow/models/FlowOutputStepInfo';
import { ISliceable } from '~src/httpClient/fetchers/ISliceable';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { LinkedTaskDialogStateContext } from '~src/linkedTasks/components/LinkedTaskDialogStateContextProvider';
import { FlowIncrementalRunAllOptions,LinkedTaskFlowState } from '~src/linkedTasks/state/LinkedTaskStateTypes';
import { filePathDisplayStringWithoutBaseName } from '~src/spacesPlatform/utils/FilePathUtils';
import { cssClass } from '~src/style';
import { applyColor }from '~src/style/ColorService';

import { getConnectionText } from '../utils/FlowDataConnectionUtils';

export const futureOutputRowHeight = 36;

const rootName = 'flowOutputSelector';

const wrapper = cssClass(rootName, 'wrapper', {
  height: '100%',
  $nest: {
    '& .tb-react-dg-hrow': { borderTop: 0 }
  },
});

const futureOutputsStyle = cssClass(rootName, 'futureCheckbox', {
  display: 'flex',
  alignItems: 'center',
  padding: '0 6px',
  height: futureOutputRowHeight,
  backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, TabStyles.Colors.F2),
});

const futureOutputsSelectorStyle = cssClass(rootName,'futureSelector', {
  marginLeft: 'auto',
});

export type FlowOutputSelectorAction =
  { type: 'selectOutput', payload: { index: number, value: string }} |
  { type: 'selectOutputs', payload: { index: number, value: { [key: string]: boolean }}} |
  { type: 'setSelectAllChecked', payload: { index: number, value: boolean }} |
  { type: 'setIsIncremental', payload: { index: number, outputId: string, value: boolean }} |
  { type: 'setFutureOutputsIncluded', payload: { index: number, value: boolean }} |
  { type: 'setAllOutputsIncremental', payload: { index: number, value: FlowIncrementalRunAllOptions }};

export type FlowOutputSelectorContentProps = {
  index: number;
  outputs: ReadonlyArray<FlowOutputStepInfo>;
}

export function FlowOutputSelectorContent(props: FlowOutputSelectorContentProps): JSX.Element {
  const [state, dispatch] = React.useContext(LinkedTaskDialogStateContext);

  React.useEffect(
    () => {
      // If futureOutputsIncluded is undefined, we have an "uninitiated" flow output selection state
      if ((state.items[props.index] as LinkedTaskFlowState).outputSelection.futureOutputsIncluded === undefined) {
        setFutureOutputsIncluded(true);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.index]
  );

  const selectionState = (state.items[props.index] as LinkedTaskFlowState).outputSelection;

  const sliceable = React.useMemo(() => {
    return getRowData(props.outputs);
  }, [props.outputs]);

  const memoizedColumns = React.useMemo(
    () => {
      return getColumns();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectionState.selectedOutputs,
      selectionState.isIncrementalMap,
      selectionState.futureOutputsIncluded,
    ]
  );

  function getColumns() {
    return FlowOutputStepGrid.columns({
      isHeaderCheckboxChecked: selectionState.selectAllChecked,
      outputsSelected: selectionState.selectedOutputs,
      outputsIsIncremental: selectionState.isIncrementalMap,
      onCheckboxClicked: onOutputSelected,
      onHeaderCheckboxClicked: onSelectAll,
      onIsIncrementalChange,
      areFutureOutputsIncluded: selectionState.futureOutputsIncluded,
    });
  }

  function getRowData(
    outputStepInfos: ReadonlyArray<FlowOutputStepInfo>
  ): () => ISliceable<any> {
    const rowArray = outputStepInfos.map(outputStep => {
      return {
        ...outputStep,
        outputLocation: filePathDisplayStringWithoutBaseName((outputStep.dataConnection.connectionDetails as FileConnectionDetail).fileName) || getConnectionText(outputStep.dataConnection),
      };
    });
    return () => SliceableHelper.fromArray(rowArray);
  }

  function onIsIncrementalChange(row: DataGrid.Row, value: boolean) {
    dispatch({ type: 'setIsIncremental', payload: { index: props.index, outputId: row.key, value }});
  }

  function onOutputSelected(row: DataGrid.Row) {
    if (selectionState.selectAllChecked) {
      setSelectAll(false);
    }
    dispatch({ type: 'selectOutput', payload: { index: props.index, value: row.key }});
  }

  function onSelectAll() {
    setSelectAll(!selectionState.selectAllChecked);
    setAllOutputs(!selectionState.selectAllChecked);
  }

  function setSelectAll(value: boolean) {
    dispatch({ type: 'setSelectAllChecked', payload: { index: props.index, value }});
  }

  function setAllOutputs(value: boolean) {
    const outputMap: {[key: string]: boolean} = {};
    props.outputs.forEach(output => {
      outputMap[output.id] = value;
    });
    dispatch({ type: 'selectOutputs', payload: { index: props.index, value: outputMap }});
  }

  function setFutureOutputsIncluded(value: boolean) {
    if (value) {
      setSelectAll(true);
      setAllOutputs(true);
    }
    dispatch({ type: 'setFutureOutputsIncluded', payload: { index: props.index, value }});
  }

  function setAllOutputsIncremental(value: string) {
    let type: FlowIncrementalRunAllOptions;
    if (value === String(null)) {
      type = 'runAllFull';
    } else if (value === String(true)) {
      type = 'runAllIncremental';
    }
    dispatch({ type: 'setAllOutputsIncremental', payload: { index: props.index, value: type }});
  }

  function futureOutputsDropdown() {
    const isIncrementalEnabled = !!props.outputs.find(output => output.isIncrementalEnabled);
    const items = FlowOutputStepGrid.futureOutputsRefreshMenuItems(isIncrementalEnabled);
    const list = <List items={items}/>;
    const value = selectionState.runAllOption === 'runAllIncremental' ? String(true) : String(null);
    const displayText = FlowOutputStepGrid.getDisplayText(items.find(item => item.value === value));
    return (
      <SingleSelectDropdown
        type={DropdownButtonType.Outline}
        list={list}
        value={value}
        testId={`future-outputs-dropdown-${props.index}`}
        onChange={setAllOutputsIncremental}
        label={''}
        aria-label={Messages.writeOptionsRefreshType()}
        disabled={!(state.items[props.index] as LinkedTaskFlowState).outputSelection.futureOutputsIncluded}
        popupAlignment='right'
      >
        {displayText}
      </SingleSelectDropdown>
    );
  }

  function futureOutputSelection() {
    const futureOutputCheckboxProps: CheckBoxWithLabelProps  = {
      label: Messages.includeAllOutputStepsCurrentAndFuture(),
      checked: !!selectionState.futureOutputsIncluded,
      handleChange: setFutureOutputsIncluded,
      testId: `future-outputs-selector-${props.index}`,
    };
    return (
      <div className={futureOutputsStyle}>
        <CheckBoxWithLabelWidget {...futureOutputCheckboxProps} />
        <div className={futureOutputsSelectorStyle}>{futureOutputsDropdown()}</div>
      </div>
    );
  }

  const dataGridProps = {
    showHorizontalGridLines: true,
  };

  return (
    <div className={wrapper}>
      <SortableDataGrid
        columns={memoizedColumns}
        getSliceable={sliceable}
        dataGridProps={dataGridProps}
      />
      {futureOutputSelection()}
    </div>
  );
}
